import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  Fragment,
} from 'react'
import styled from 'styled-components'
import { keyBy, values } from 'lodash/fp'
import moment from 'moment'

import { AppContext } from '../App'
import Notifier from '../Notifications'
import colors from '../../styles/colors'
import { ID } from '../../types/id'
import PostDiagnosticModal from '../PostDiagnostic/PostDiagnosticModal'
import Table from '../../components/Table'
import { InquiryPanel } from '../Inquiry/InquiryPanel'
import withLoading from '../../utils/withLoading'

// @ts-ignore @todo add apprioprate declaration file
import noteIcon from '../../images/Note.svg'
// @ts-ignore @todo add apprioprate declaration file
import copyIcon from '../../images/copy.svg'
// @ts-ignore @todo add apprioprate declaration file
import formIcon from '../../images/form.svg'

import NoteModal from './NoteModal'
import { disabilityByValue, statusLabels } from './constants'
import { navigate } from 'gatsby'
import { Hidden } from '../../styles/Shared'
import { DiagnosticModalProps } from '../PostDiagnostic/utils'

const getDiagnosisById = (list) => keyBy('id', list)
const normalizeDate = (date) => moment(date).format('YYYY-MM-DD')
const LoadingTable = withLoading(Table)

const List = ({ params, setParams, diagnosis, setDiagnosis, ready }) => {
  const { withAuth } = useContext(AppContext)
  const { notify } = useContext(Notifier)

  const [inquiry, setInquiry] = useState<null | { code: string; id: ID }>(null)
  const [modalState, setModalState] = useState({
    open: false,
    id: null,
  })
  const [diagnosticModal, setDiagnosticModal] = useState<DiagnosticModalProps>({
    open: false,
    examinationId: null,
  })

  const diagnosisById = useMemo(() => getDiagnosisById(diagnosis), [diagnosis])

  const copyCode = useCallback(
    async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        notify(`Kod ${text} został skopiowany do schowka`, 'success')
      } catch (e) {
        notify('Nie udało się skopiować kodu', 'error')
      }
    },
    [notify]
  )

  const submitNote = useCallback(
    (vals) => {
      const modifyNote = async () => {
        const result = await withAuth(
          `/diagnostic/rest/examination/${modalState.id}/note`,
          {
            method: 'PUT',
            payload: {
              note: vals.note ? vals.note : '',
            },
          }
        )
        result
          ? notify('Notatki zostały zapisane', 'success')
          : notify('Nie udało się zapisac notatki, spróbuj ponownie', 'error')
        setModalState({ open: false, id: null })
      }
      modifyNote()
      const updatedDiagnosis = {
        ...diagnosisById,
        [modalState.id]: {
          ...diagnosisById[modalState.id],
          note: vals.note ? vals.note : '',
        },
      }
      setDiagnosis(values(updatedDiagnosis))
    },
    [diagnosisById, modalState.id, notify, withAuth]
  )

  const onPageChange = (index) => {
    setParams({ ...params, meta: { ...params.meta, page: index } })
  }

  return (
    <Fragment>
      {inquiry && (
        <InquiryPanel {...inquiry} onClose={() => setInquiry(null)} />
      )}
      <LoadingTable
        page={params.meta.page}
        total={params.meta?.total}
        defaultPageSize={params.meta.limit}
        onPageChange={(index) => onPageChange(index)}
        getLoadingProps={() => ({
          'aria-hidden': true,
          role: 'none',
        })}
        order={params.sorts}
        data={diagnosis}
        ready={ready}
        getTheadTrProps={() => ({
          style: { display: 'flex', alignItems: 'center' },
        })}
        columns={[
          {
            Header: 'Data',
            id: 'expectedDate',
            sortable: false,
            accessor: ({ expectedDate }) => normalizeDate(expectedDate),
            getProps: () => ({
              role: undefined,
            }),
          },

          {
            Header: 'Grupa SPE',
            sortable: false,
            id: 'disability',
            accessor: ({ patient }) =>
              disabilityByValue[patient.disability].label,
            getProps: () => ({
              role: undefined,
            }),
          },

          {
            Header: 'Status',
            id: 'status',
            sortable: false,
            accessor: ({ status }) => statusLabels[status],
            getProps: () => ({
              role: undefined,
            }),
          },
          {
            Header: 'Kod badania',
            sortable: false,
            width: 200,
            Cell: ({ original }) => (
              <FlexWrap>
                <span>{original.code}</span>
                <IconButton
                  type="button"
                  onClick={() => copyCode(original.code)}
                >
                  kopiuj
                  <Img alt="" src={copyIcon} aria-hidden="true" />
                </IconButton>
              </FlexWrap>
            ),
          },
          {
            Header: 'Skale i Raporty',
            sortable: false,
            width: 200,
            Cell: ({ original }) => (
              <FlexWrap>
                <IconButton
                  type="button"
                  onClick={() => {
                    setInquiry({ id: original.id, code: original.code })
                  }}
                >
                  Skale
                  <Img alt="" src={formIcon} aria-hidden="true" />
                </IconButton>
                {original.inquired || original.status === 'DONE' ? (
                  <IconButton
                    type="button"
                    onClick={() =>
                      navigate(`report`, {
                        state: {
                          diagnosis: original,
                        },
                      })
                    }
                  >
                    Raporty
                    <Img alt="" src={formIcon} aria-hidden="true" />
                  </IconButton>
                ) : null}
              </FlexWrap>
            ),
          },

          {
            Header: 'Notatki',
            sortable: false,
            Cell: ({ original }) => (
              <IconButton
                type="button"
                onClick={() =>
                  setModalState({
                    open: true,
                    id: original.id,
                  })
                }
                height="100%"
              >
                <Hidden>Pokaż notatkę</Hidden>
                <Img alt="" src={noteIcon} aria-hidden="true" />
              </IconButton>
            ),
          },
          {
            Header: 'Materiały postdiagnostyczne',
            sortable: false,
            Cell: (id) => (
              <IconButton
                type="button"
                onClick={() => {
                  setDiagnosticModal({
                    open: true,
                    examinationId: id.original.id,
                  })
                }}
              >
                Zobacz materiały
                <Img alt="" src={formIcon} aria-hidden="true" />
              </IconButton>
            ),
          },
        ]}
      />
      <NoteModal
        isOpen={modalState.open}
        onClose={() => {
          setModalState({ open: false, id: null })
        }}
        note={modalState.id ? diagnosisById[modalState.id].note : ''}
        onSubmit={submitNote}
      />
      {diagnosticModal.open && (
        <PostDiagnosticModal
          examinationId={diagnosticModal.examinationId}
          isOpen={diagnosticModal.open}
          onClose={() =>
            setDiagnosticModal({ open: false, examinationId: null })
          }
        />
      )}
    </Fragment>
  )
}

const FlexWrap = styled.div`
  display: flex;
  justify-content: space-between;
  margin-right: 5px;
`

const IconButton = styled.button`
  display: flex;
  margin: 0;
  background: white;
  border: 1px solid ${colors.light_black};
  &:hover {
    border: 1px solid ${colors.mantis_green};
  }
  &.danger {
    &:hover {
      border: 1px solid ${colors.red};
    }
  }
  align-items: center;
  cursor: pointer;
  height: ${({ height }) => (height != null ? height : 'auto')};
  border-radius: 5px;
  > * {
    padding: 0 0 0 3px;
  }
`

const Img = styled.img`
  margin-bottom: 0;
  cursor: pointer;
`

export default List
